import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppValidators } from 'src/app/common/appValidators';
import {
  CustomWebhookHeader,
  SecretWebhookHeader,
  SendWebhookActionDefinitionModel,
} from 'src/app/models/api/playbooks/actions/sendWebhookProperties';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import { PlaybooksActionDefinitionInput } from 'src/app/models/componentInputs/playbookActionInput.interface';

const MAX_HEADERS = 20;

@Component({
  selector: 'app-send-webhook-action',
  templateUrl: './send-webhook-action.component.html',
  styleUrls: ['./send-webhook-action.component.scss'],
})
export class SendWebhookActionComponent implements OnInit {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksActionInput: PlaybooksActionDefinitionInput;
  @Input() public triggerType: TriggerTypes;

  public hasChange = false;
  private _newSendWebhookActionDefinitionModel?: SendWebhookActionDefinitionModel;

  get secretHeaders(): FormArray {
    return this.fg.get('secretHeaders') as FormArray;
  }

  get customHeaders(): FormArray {
    return this.fg.get('customHeaders') as FormArray;
  }

  get canAddSecretHeader(): boolean {
    return this.secretHeaders.length < MAX_HEADERS;
  }

  get canAddCustomHeader(): boolean {
    return this.customHeaders.length < MAX_HEADERS;
  }

  ngOnInit(): void {
    let url = '';
    if (this.playbooksActionInput.actionDefinitionProperties) {
      this._newSendWebhookActionDefinitionModel = this.playbooksActionInput
        .actionDefinitionProperties as SendWebhookActionDefinitionModel;
      url = this._newSendWebhookActionDefinitionModel.url;
    }

    this.fg.addControl('url', new UntypedFormControl(url.trim(), [Validators.required, AppValidators.secureUrl]));

    // Initialize secret headers FormArray
    const secretHeadersArray = new FormArray<FormGroup>([]);
    if (this._newSendWebhookActionDefinitionModel?.secretHeaders) {
      for (const header of this._newSendWebhookActionDefinitionModel.secretHeaders) {
        secretHeadersArray.push(this.createSecretHeaderGroup(header));
      }
    }
    this.fg.addControl('secretHeaders', secretHeadersArray);

    // Initialize custom headers FormArray
    const customHeadersArray = new FormArray<FormGroup>([]);
    if (this._newSendWebhookActionDefinitionModel?.customHeaders) {
      for (const header of this._newSendWebhookActionDefinitionModel.customHeaders) {
        customHeadersArray.push(this.createCustomHeaderGroup(header));
      }
    }
    this.fg.addControl('customHeaders', customHeadersArray);
  }

  addSecretHeader(): void {
    if (this.canAddSecretHeader) {
      this.secretHeaders.push(this.createSecretHeaderGroup());
    }
  }

  removeSecretHeader(index: number): void {
    this.secretHeaders.removeAt(index);
  }

  addCustomHeader(): void {
    if (this.canAddCustomHeader) {
      this.customHeaders.push(this.createCustomHeaderGroup());
    }
  }

  removeCustomHeader(index: number): void {
    this.customHeaders.removeAt(index);
  }

  private createSecretHeaderGroup(header?: SecretWebhookHeader): FormGroup {
    return new FormGroup({
      id: new UntypedFormControl(header?.id || null),
      name: new UntypedFormControl(header?.name || '', [Validators.required, Validators.maxLength(256)]),
      value: new UntypedFormControl(header?.value || '', [Validators.required, Validators.maxLength(4096)]),
    });
  }

  private createCustomHeaderGroup(header?: CustomWebhookHeader): FormGroup {
    return new FormGroup({
      name: new UntypedFormControl(header?.name || '', [Validators.required, Validators.maxLength(256)]),
      value: new UntypedFormControl(header?.value || '', [Validators.maxLength(4096)]),
    });
  }
}
